import React, { useState } from 'react';
import {
  Avatar,
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  People,
  Description,
  Home as Farm,
  CalendarToday,
  AttachMoney,
  Receipt,
  Newspaper,
  Logout,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AssociationLogo from './AssociationLogo';
import { branding } from '../config/branding';

const drawerWidth = 268;
const topAccentHeight = 6;

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const menuItems = [
    ...(user?.acesso_todos ? [{ text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' }] : []),
    { text: 'Sócios', icon: <People />, path: '/socios' },
    { text: 'Documentos', icon: <Description />, path: '/documentos' },
  ];

  if (user?.acesso_todos) {
    menuItems.push(
      { text: 'Fazendas', icon: <Farm />, path: '/fazendas' },
      { text: 'Agendamentos', icon: <CalendarToday />, path: '/agendamentos' },
      { text: 'Financeiro', icon: <AttachMoney />, path: '/financeiro' },
      { text: 'Anuidades', icon: <Receipt />, path: '/anuidades' },
      { text: 'Notícias do site', icon: <Newspaper />, path: '/noticias-site' }
    );
  }

  const currentPage = menuItems.find((item) => item.path === location.pathname)?.text ?? 'Painel';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userInitial = user?.nome?.trim()?.charAt(0)?.toUpperCase() ?? '?';

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: branding.colors.sidebar }}>
      <Box
        sx={{
          px: 2,
          py: 2.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <AssociationLogo size={58} variant="horizontal" expandOnClick />
      </Box>

      <List sx={{ flex: 1, px: 1, py: 1 }}>
        {menuItems.map((item) => {
          const selected = location.pathname === item.path;
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                selected={selected}
                onClick={() => {
                  navigate(item.path);
                  setMobileOpen(false);
                }}
                sx={{
                  borderRadius: 2,
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    '& .MuiListItemIcon-root': { color: 'primary.contrastText' },
                    '&:hover': { bgcolor: 'primary.dark' },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: selected ? 600 : 500 }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Divider />
      <List sx={{ px: 1, py: 1 }}>
        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout} sx={{ borderRadius: 2 }}>
            <ListItemIcon sx={{ minWidth: 40 }}>
              <Logout />
            </ListItemIcon>
            <ListItemText primary="Sair" primaryTypographyProps={{ fontWeight: 500 }} />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: branding.colors.surface }}>
      <Box
        aria-hidden
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: topAccentHeight,
          bgcolor: branding.colors.primary,
          zIndex: (theme) => theme.zIndex.drawer + 2,
        }}
      />

      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          top: topAccentHeight,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar sx={{ gap: 2 }}>
          <IconButton
            color="inherit"
            aria-label="abrir menu"
            edge="start"
            onClick={() => setMobileOpen(true)}
            sx={{ display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="h6" noWrap sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {currentPage}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Box sx={{ textAlign: 'right', minWidth: 0, display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                {user?.nome}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {user?.acesso_todos ? 'Administrador' : 'Associado'}
              </Typography>
            </Box>
            <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36, fontSize: '0.95rem' }}>
              {userInitial}
            </Avatar>
          </Box>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              top: topAccentHeight,
              height: `calc(100% - ${topAccentHeight}px)`,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              top: topAccentHeight,
              height: `calc(100% - ${topAccentHeight}px)`,
              borderRight: '1px solid',
              borderColor: 'divider',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar sx={{ mt: `${topAccentHeight}px` }} />
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
